import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import { Stage } from "developer/formsCRUD/store/formDefinitionStagesSlice";
import { FormDefinition } from "developer/formsCRUD/store/formDefinitionsSlice";
import { useCallback, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
  ResponderProvided,
} from "react-beautiful-dnd";

type StagesTabPanelProps = {
  stages: Stage[];
  initialStageName: string | null;
  onSubmit: () => void;
  formDefinitions: FormDefinition[];
  hasStagesError: boolean;
  onClickAddStage: () => void;
  onChangeStage: (index: number, newStage: Partial<Stage>) => void;
  onChangeInitialStage: (value: string) => void;
  onDeleteStage: (index: number) => void;
  onDragEndStage: (result: DropResult, provided: ResponderProvided) => void;
};

const NoStages = () => {
  return (
    <Box
      height="100%"
      width="100%"
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
    >
      <Typography variant="body2" color="textSecondary">
        The form has no stages assigned.
      </Typography>
    </Box>
  );
};

const GridAllHeight = styled(Grid)({
  height: "100%",
});

const GridContentScrollable = styled(Grid)({
  overflow: "auto",
});

const StagesTab = (props: StagesTabPanelProps) => {
  const {
    stages,
    initialStageName,
    onClickAddStage,
    onChangeStage,
    formDefinitions,
    onDeleteStage,
    onDragEndStage,
    hasStagesError,
    onChangeInitialStage,
    onSubmit,
  } = props;

  const [shouldSetFocus, setShouldSetFocus] = useState<boolean>(false);

  useEffect(() => {
    if (shouldSetFocus) {
      setShouldSetFocus(false);
    }
  }, [shouldSetFocus]);

  const onAddStage = useCallback(() => {
    setShouldSetFocus(true);
    onClickAddStage();
  }, [onClickAddStage]);

  return (
    <GridAllHeight container direction="column" spacing={3}>
      <Grid container item justifyContent="flex-end">
        <Grid item>
          <Button
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddStage}
          >
            Add Stage
          </Button>
        </Grid>
      </Grid>
      <GridContentScrollable container item xs wrap="nowrap" spacing={2}>
        {stages.length === 0 ? (
          <NoStages />
        ) : (
          <Box height="100%" width="100%" maxHeight={500} overflow="auto">
            <DragDropContext onDragEnd={onDragEndStage}>
              <Droppable droppableId="stages">
                {(droppableProvider) => (
                  <List
                    dense
                    {...droppableProvider.droppableProps}
                    ref={droppableProvider.innerRef}
                  >
                    <ListItem alignItems="center">
                      <ListItemAvatar>
                        <DragIndicatorIcon color="disabled" />
                      </ListItemAvatar>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            value={initialStageName}
                            size="small"
                            fullWidth
                            label="Initial Stage Name"
                            onChange={(event) =>
                              onChangeInitialStage(event.target.value)
                            }
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                    {stages.map((stage, index) => (
                      <Draggable
                        key={index}
                        draggableId={`${index}`}
                        index={index}
                      >
                        {(draggableProvider) => (
                          <ListItem
                            {...draggableProvider.draggableProps}
                            {...draggableProvider.dragHandleProps}
                            ref={draggableProvider.innerRef}
                            alignItems="center"
                          >
                            <ListItemAvatar>
                              <DragIndicatorIcon />
                            </ListItemAvatar>
                            <Grid
                              container
                              spacing={2}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Grid item xs={6}>
                                <TextField
                                  value={stage.name}
                                  size="small"
                                  error={hasStagesError && !stage.name}
                                  helperText={
                                    hasStagesError && !stage.name
                                      ? "Required!"
                                      : undefined
                                  }
                                  label={`Stage ${index + 1} Name`}
                                  fullWidth
                                  autoFocus={
                                    shouldSetFocus &&
                                    index === stages.length - 1
                                  }
                                  onChange={(event) =>
                                    onChangeStage(index, {
                                      name: event.target.value,
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  select
                                  size="small"
                                  error={
                                    hasStagesError && !stage.formDefinitionId
                                  }
                                  fullWidth
                                  label="Form Definition"
                                  value={stage.formDefinitionId}
                                  helperText={
                                    hasStagesError && !stage.formDefinitionId
                                      ? "Required!"
                                      : undefined
                                  }
                                  onChange={(event) =>
                                    onChangeStage(index, {
                                      formDefinitionId: event.target.value,
                                    })
                                  }
                                >
                                  {formDefinitions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                      {item.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            </Grid>
                            <Box width={40}>
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => onDeleteStage(index)}
                                  size="large"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </Box>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvider.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        )}
      </GridContentScrollable>
      <Grid container item justifyContent="flex-end">
        <Grid item>
          <Button variant="contained" color="primary" onClick={onSubmit}>
            Save
          </Button>
        </Grid>
      </Grid>
    </GridAllHeight>
  );
};

export default StagesTab;
